"use client";
import React from "react";

import { Box, BoxAnchor, BoxButton, BoxDiv, BoxSx } from "../../foundations";

interface TargetContextValue {
  clickHandler: (e: React.MouseEvent) => void;
  targetResolver?: (
    href?: string
  ) => TargetResolverOverrides | string | undefined;
}

const TargetContext = React.createContext<TargetContextValue>({
  clickHandler: () => {}
});

interface TargetResolverOverrides {
  href?: BoxAnchor["href"];
  target?: BoxAnchor["target"];
}

export const TargetProvider: React.FC<{
  children: React.ReactNode;
  clickHandler: (e: React.MouseEvent) => void;
  targetResolver?: TargetContextValue["targetResolver"];
}> = ({ children, clickHandler, targetResolver }) => {
  return (
    <TargetContext.Provider value={{ clickHandler, targetResolver }}>
      {children}
    </TargetContext.Provider>
  );
};

type TargetProps = { disabled?: boolean } & ((
  | Omit<BoxAnchor, "as" | "onClick">
  | Omit<BoxButton, "as" | "onClick">
  | Omit<BoxDiv, "as" | "onClick">
) & {
  onClick?: React.MouseEventHandler;
});

export const Target = React.forwardRef<HTMLElement, TargetProps>(
  ({ children, disabled, onClick, sx, ...props }, ref) => {
    const { clickHandler, targetResolver } = React.useContext(TargetContext);

    const hrefResolved = React.useMemo(() => {
      if ("href" in props) {
        const resolved = targetResolver?.(props.href);

        if (typeof resolved === "object" && resolved !== null) {
          return resolved.href;
        }

        return props.href;
      }

      return undefined;
    }, [props, targetResolver]);
    const targetResolved = React.useMemo(() => {
      if (!targetResolver) {
        if ("target" in props) {
          return props.target;
        }
      } else {
        if ("href" in props) {
          const resolved = targetResolver(props.href);

          if (typeof resolved === "string") {
            return resolved;
          } else if (typeof resolved === "object" && resolved !== null) {
            return resolved.target;
          }
        }
      }
      return undefined;
    }, [props, targetResolver]);

    const cursor = React.useMemo<BoxSx["cursor"]>(() => {
      if (sx?.cursor) {
        return sx?.cursor;
      } else if (disabled) {
        return "not-allowed";
      } else if (
        "href" in props ||
        ("type" in props && props.type === "submit") ||
        onClick
      ) {
        return "pointer";
      } else {
        return "default";
      }
    }, [disabled, onClick, props, sx?.cursor]);

    const handleClick = React.useCallback(
      (e: React.MouseEvent) => {
        if (disabled) {
          e.preventDefault();
        } else {
          onClick?.(e);

          if (!e.defaultPrevented) {
            clickHandler(e);
          }
        }
      },
      [clickHandler, disabled, onClick]
    );

    if (hrefResolved) {
      const { ...anchorProps } = props as BoxAnchor;

      return (
        <Box
          {...anchorProps}
          as="a"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          href={hrefResolved}
          onClick={handleClick}
          sx={{ ...sx, cursor }}
          ref={ref}
          target={targetResolved}
        >
          {children}
        </Box>
      );
    } else {
      const { role = "button", ...buttonProps } = props as BoxButton;

      return (
        <Box
          {...buttonProps}
          as="button"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          role={role}
          sx={{ ...sx, cursor }}
          onClick={handleClick}
          ref={ref}
        >
          {children}
        </Box>
      );
    }
  }
);

Target.displayName = "Target";
